import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box, Typography, Paper, Grid, Chip, Avatar, Table, TableBody, Checkbox,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  FormControl, Select, MenuItem, InputLabel, TextField,
  IconButton, Card, CardContent, Button, Tab, Tabs, Dialog, DialogTitle, Popover,
  DialogContent, DialogActions, CircularProgress, useMediaQuery, Tooltip, Alert, InputAdornment, FormControlLabel, RadioGroup, Radio,
  Snackbar, Modal,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  KeyboardArrowDown,
  Group,
  Settings,
  FilterList,
  Description,
  Tune,
  Clear,
  Token
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { localAxiosGet, axiosInstance, localAxios } from '../../../Axios/axios';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  Search, Edit, Refresh, CalendarToday, FilterAlt, MoreVert, CheckCircle,
  Cancel, AccessTime, Dashboard, History, Close, Event, EventBusy, EventAvailable,
  KeyboardArrowLeft, KeyboardArrowRight, Timeline, PersonOff, PeopleAlt, SearchOff, InsertChart, LocationOn,
  LocationOff, EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, eachDayOfInterval } from 'date-fns';
import EmployeeAttendanceCalendar from '../../../components/MyAttendance';


const EmployeeAttendanceHistory = ({ employee, open, onClose }) => {
  const theme = useTheme();
  const token = useSelector(state => state.auth.accessToken);
  const [historyData, setHistoryData] = useState({
    attendanceRate: "0%",
    presentCount: 0,
    absentCount: 0,
    lateCount: 0,
    leaveCount: 0,
    totalWorkingDays: 0,
    attendanceRecords: []  
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);  

  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
  const currentYear = currentDate.getFullYear();

  const [historyFilters, setHistoryFilters] = useState({
    month: currentMonth,
    year: currentYear.toString()
  });
  // New state for status filter
  const [selectedStatus, setSelectedStatus] = useState('All');

  const [anchorEl, setAnchorEl] = useState(null);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const [snackbar, setSnackbar] = useState({
    open: 'false',
    message: '',
    severity: 'sucess'
  })

  const fetchEmployeeHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {  
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Use the correct endpoint format without /api prefix
      const endpoint = `/organization/getAttendanceOverviewByEmployeeIdByMonthForHr?employee_id=${employee.user_id}&month=${historyFilters.month}&year=${historyFilters.year}&status=${selectedStatus}`;

      // Use your localAxiosGet helper function
      const config = localAxiosGet(endpoint, token);
      const response = await axiosInstance(config);

      if (response.data && response.data.status === "SUCCESS") {
        const data = response.data.message;

        // Format the attendance records according to the response format
        const formattedRecords = data.attendanceRecords.map(record => {
          const recordDate = new Date(record.date);

          // Format check-in and check-out times
          const checkIn = record.check_in
            ? new Date(record.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '-';

          const checkOut = record.check_out
            ? new Date(record.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '-';

          const inOfficeCheckIn = record.office_premises?.check_in?.in_office;
          // Calculate production hours if both check-in and check-out exist
          let productionHours = '-';
          let isPositive = false;

          if (record.check_in && record.check_out) {
            const checkInTime = new Date(record.check_in);
            const checkOutTime = new Date(record.check_out);

            // Calculate hours difference
            const diffMs = checkOutTime - checkInTime;
            const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            productionHours = `${diffHrs}h ${diffMins}m`;
            isPositive = (diffHrs >= 7 || (diffHrs === 6 && diffMins >= 30));
          }

          return {
            date: recordDate,
            day: recordDate.toLocaleDateString('en-US', { weekday: 'long' }),
            status: record.status,
            checkIn: checkIn,
            checkOut: checkOut,
            productionHours: productionHours,
            isPositive: isPositive,
            inOfficeCheckIn: inOfficeCheckIn,
          };
        });

        setHistoryData({
          attendanceRate: data.attendanceRate,
          presentCount: data.presentCount,
          absentCount: data.absentCount,
          lateCount: data.lateCount,
          leaveCount: data.leaveCount,
          totalWorkingDays: data.totalWorkingDays,
          attendanceRecords: formattedRecords,
          wfhCount: data.workFromHomeCount,
        });
      } else {
        throw new Error(response.data?.message?.msg || 'Failed to fetch employee history');
      }
    } catch (err) {
      console.error('Error fetching employee history:', err);
      setError(err.message || 'Failed to fetch employee history');
    } finally {
      setLoading(false);
    }
  }, [employee.user_id, historyFilters.month, historyFilters.year]);

  useEffect(() => {
    if (open && employee && employee.user_id) {
      fetchEmployeeHistory();
    }
  }, [open, employee, fetchEmployeeHistory, historyFilters]);

  // Update this to conditionally filter the records
  const filteredHistory = historyData.attendanceRecords.filter(record => {
    if (selectedStatus === 'All') {
      return true;
    }
    // Normalize status for case-insensitive comparison
    return record.status.toLowerCase() === selectedStatus.toLowerCase();
  });


  const handleMonthChange = (month) => {
    setHistoryFilters(prev => ({
      ...prev,
      month: month
    }));
  };

  // New handler for status change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const years = [];
  for (let i = 2; i >= 0; i--) {
    years.push(currentYear - i);
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthMap = {};
  months.forEach((month, index) => {
    monthMap[month] = index + 1;
  });

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleFormatChange = (event) => {
    setDownloadFormat(event.target.value);
  };

  const handleDownload = async () => {
    const monthNumber = monthMap[historyFilters.month];
    try {
      const endpoint = downloadFormat === 'excel'
        ? `/organization/getEmployeeDailyAttendanceExcel?emp_id= ${employee.user_id}&month=${monthNumber}&year=${historyFilters.year}&status=${selectedStatus}`

        : `/organization/getEmployeeDailyAttendancePDF?emp_id=${employee.user_id}&month=${monthNumber}&year=${historyFilters.year}&status=${selectedStatus}`;

      const response = await axiosInstance({
        ...localAxiosGet(endpoint, token),
        responseType: 'blob'
      });

      if (!(response.data instanceof Blob)) {
        throw new Error('Response data is not a Blob');
      }

      // Determine content type and filename as you already have
      const contentType = response.headers['content-type'] ||
        (downloadFormat === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf');
      const extension = downloadFormat === 'excel' ? 'xlsx' : 'pdf';
      let filename = `attendance_report_${historyFilters.month}_${historyFilters.year}.${extension}`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create and trigger download
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);


      setSnackbar({
        open: true,
        message: 'Download started successfully!',
        severity: 'success'
      });

      // You can still keep your other cleanup logic for the dialog
      setTimeout(() => {
        setAnchorEl(null);
        setDownloadFormat('')
      }, 6000);

    } catch (error) {
      console.error('Download failed:', error);

      setSnackbar({
        open: true,
        // message: `Failed to download: ${error.message || 'An unknown error occurred.'}`,
        message: 'Failed to download. Please try again',
        severity: 'error'
      });
    }
  }
  const popoverOpenFordownloadFormat = Boolean(anchorEl);

  const handleYearChange = (year) => {
    setHistoryFilters(prev => ({
      ...prev,
      year: year
    }));
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late':
      case 'latearrival': return 'warning';
      case 'leave': return 'info';
      default: return 'default';
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.8)}, ${alpha(theme.palette.primary.main, 0.8)})`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {employee.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>

              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {employee.department}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    background: alpha(theme.palette.background.default, 0.5)
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FilterList sx={{ mr: 1.5, color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Attendance History
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                    <FormControl size='small' sx={{ minWidth: 100 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={selectedStatus}
                        label='Status'
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        {['All', 'Present', 'Absent', 'LateArrival', 'Leave'].map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={historyFilters.month}
                        label="Month"
                        onChange={(e) => handleMonthChange(e.target.value)}
                        sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center' } }}
                      >
                        {['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'].map((month) => (
                            <MenuItem key={month} value={month}>
                              {month}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={historyFilters.year}
                        label="Year"
                        onChange={(e) => handleYearChange(e.target.value)}
                      >
                        {years.map(year => (
                          <MenuItem key={year} value={year.toString()}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '30px',
                      }}
                    >
                      <IconButton onClick={handleOpenPopover}>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>

                    <Popover
                      open={popoverOpenFordownloadFormat}
                      anchorEl={anchorEl}
                      onClose={handleClosePopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      <Box sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <FormControl component="fieldset">
                          <RadioGroup
                            aria-label="download-format"
                            name="download-format-group"
                            value={downloadFormat}
                            onChange={handleFormatChange}
                          >
                            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
                            <FormControlLabel value="excel" control={<Radio />} label="Excel" />
                          </RadioGroup>
                        </FormControl>
                        <Button onClick={handleDownload} variant="contained" sx={{ mt: 1 }}>
                          Download
                        </Button>
                      </Box>
                    </Popover>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Refresh />}
                      onClick={fetchEmployeeHistory}
                      disabled={loading}
                      sx={{ height: 40 }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Paper>


                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Attendance Rate
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {historyData.attendanceRate}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Present Days
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                        <Box>
                          <Typography variant="h4" fontWeight="bold" color="success.main">
                            {historyData.presentCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
                          <Chip
                            label={`WFH: ${historyData.wfhCount}`}
                            size="small"
                            sx={{
                              backgroundColor: 'transparent',
                              color: theme.palette.info.dark,
                              border: `1px solid ${theme.palette.info.light}`,
                              '& .MuiChip-label': {
                                fontSize: '0.6rem',
                              },
                            }}
                          />
                          <Chip
                            label={`WFO: ${Math.max(0, historyData.presentCount - historyData.wfhCount)}`}
                            size="small"
                            sx={{
                              backgroundColor: 'transparent',
                              color: theme.palette.warning.dark,
                              border: `1px solid ${theme.palette.warning.light}`,
                              '& .MuiChip-label': {
                                fontSize: '0.6rem',
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Absent Days
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="error.main">
                        {historyData.absentCount}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Late Arrivals
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {historyData.lateCount}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Leaves
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="green">
                        {historyData.leaveCount}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>


                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Day</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Check In</TableCell>
                        <TableCell>Check Out</TableCell>
                        <TableCell>Production Hours</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHistory.length > 0 ? (
                        filteredHistory.map((record, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                  {record.date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </Typography>
                                {record.checkIn !== '-' && record.inOfficeCheckIn === 'no' && (
                                  <Chip
                                    label="WFH"
                                    size="small"
                                    sx={{
                                      backgroundColor: 'transparent',
                                      color: theme.palette.info.dark,
                                      border: `1px solid ${theme.palette.info.light}`,
                                      '& .MuiChip-label': {
                                        fontSize: '0.6rem',
                                        lineHeight: '1',
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{record.day}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.status}
                                size="small"
                                color={getStatusColor(record.status)}
                              />
                            </TableCell>
                            <TableCell>{record.checkIn}</TableCell>
                            <TableCell>{record.checkOut}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: record.isPositive ? 'success.main' : 'text.primary'
                                  }}
                                >
                                  {record.productionHours}
                                </Typography>
                                {record.isPositive && (
                                  <CheckCircle
                                    fontSize="small"
                                    color="success"
                                    sx={{ ml: 1, fontSize: 16 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                              No attendance records found for this period
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.default,
            justifyContent: 'flex-end'
          }}
        >
          <Button
            onClick={() => {
              const currentDate = new Date();
              const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
              const currentYear = currentDate.getFullYear();

              setHistoryFilters({
                month: currentMonth,
                year: currentYear.toString()
              });
              // Resetting status filter when closing the dialog
              setSelectedStatus('All');

              onClose();
            }}
            variant="outlined"
            color="primary"
          >
            Close
          </Button>
          {/* <Button
            variant="contained"
            color="primary"
            onClick={fetchEmployeeHistory}
            startIcon={<Refresh />}
            disabled={loading}
            sx={{ ml: 2 }}
          >
            Refresh Data
          </Button> */}
        </DialogActions>
      </Dialog>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const AttendanceSummary = ({ summaryData }) => {
  const theme = useTheme();

  const metrics = [
    {
      key: "total",
      label: "Total Employees",
      value: summaryData.total,
      icon: PeopleAlt,
      color: "#4361EE",
      lightColor: "#EDF2FB"
    },
    {
      key: "present",
      label: "Present Today",
      value: summaryData.present?.count || 0,
      percentage: summaryData.present?.percentage || '0%',
      icon: CheckCircle,
      color: "#2EC4B6",
      lightColor: "#E8F8F5"
    },
    {
      key: "late",
      label: "Late Arrivals",
      value: summaryData.lateLogin?.count || 0,
      percentage: summaryData.lateLogin?.percentage || '0%',
      icon: AccessTime,
      color: "#FF9F1C",
      lightColor: "#FFF4E6"
    },
    {
      key: "absent",
      label: "Absent Today",
      value: summaryData.absent?.count || 0,
      percentage: summaryData.absent?.percentage || '0%',
      icon: PersonOff,
      color: "#E71D36",
      lightColor: "#FDECEF"
    },
    {
      key: "leaves",
      label: "Leaves Today",
      value: summaryData.leave?.count || 0,
      percentage: summaryData.leave?.percentage || '0%',
      icon: EventBusy,
      color: "#7B2CBF",
      lightColor: "#F3E8FF"
    }
  ];


  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-start' },
          flexWrap: 'wrap',
          gap: 3,
          px: { xs: 1, sm: 2 },
          py: 3
        }}
      >
        {metrics.map((metric) => (
          <Box
            key={metric.key}
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 24px)', md: '1 1 calc(33% - 24px)', lg: '1 1 calc(17% - 24px)' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 24px)', md: 'calc(33% - 24px)', lg: 'calc(20% - 24px)' },
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              background: metric.lightColor,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 10px 20px ${alpha(metric.color, 0.15)}`
              }
            }}
          >
            {/* Icon Box */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${metric.color}, ${alpha(metric.color, 0.8)})`,
                boxShadow: `0 4px 10px ${alpha(metric.color, 0.3)}`,
                color: 'white',
              }}
            >
              <metric.icon sx={{ fontSize: 30 }} />
            </Box>

            {/* Content Box */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: alpha(theme.palette.text.primary, 0.7),
                  mb: 0.5,
                  fontWeight: 500
                }}
              >
                {metric.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: metric.color,
                    lineHeight: 1
                  }}
                >
                  {metric.value}
                </Typography>
                {metric.percentage && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: metric.color,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {metric.percentage}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const AttendanceTable = ({
  employeeData,
  onPageChange,
  onRowsPerPageChange,
  page,
  rowsPerPage,
  selectedDate,
  onDateChange,
  onViewEmployeeHistory
}) => {
  const theme = useTheme();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [filters, setFilters] = useState({
    searchQuery: '',
    status: 'All',
    location: 'All',
    inOfficeCheckIn: 'All',
    inOfficeCheckOut: 'All',
    locationId: 'All'
  });

  // Fetch locations on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);  
        const orgId = localStorage.getItem('organizationId');

        if (!token || !orgId) return;
        const response = await axiosInstance(localAxiosGet(`/organization/getAllLocationsByOrgId/${orgId}`, token))

        if (response.data && response.data.status === "SUCCESS") {
          setLocations(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late':
      case 'latearrival': return 'warning';
      case 'leave': return 'info'
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return null;
    switch (status.toLowerCase()) {
      case 'present': return <CheckCircle fontSize="small" />;
      case 'absent': return <Cancel fontSize="small" />;
      case 'late':
      case 'latearrival': return <AccessTime fontSize="small" />;
      case 'leave': return <EventBusy fontSize="small" />;
      default: return null;
    }
  };

  const handleViewHistory = (employee, event) => {
    if (event) event.stopPropagation();
    if (onViewEmployeeHistory) onViewEmployeeHistory(employee);
  };

  const filteredEmployeeData = useMemo(() => {
    let result = [...employeeData];

    // Apply search filter
    if (filters.searchQuery.trim()) {
      const lowerCaseQuery = filters.searchQuery.toLowerCase();
      result = result.filter(employee =>
        (employee.name && employee.name.toLowerCase().includes(lowerCaseQuery)) ||
        (employee.department && employee.department.toLowerCase().includes(lowerCaseQuery))
      );
    }

    // Apply status filter
    if (filters.status && filters.status !== 'All') {
      result = result.filter(employee =>
        employee.status && employee.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.locationId && filters.locationId !== 'All') {
      const selectedLocation = locations.find(loc => loc.id.toString() === filters.locationId.toString());
      const selectedLocationName = selectedLocation ? selectedLocation.location_name : null;

      if (selectedLocationName) {
        result = result.filter(employee =>
          employee.location && employee.location === selectedLocationName
        );
      }
    }

    // Apply In Office CheckIn filter
    if (filters.inOfficeCheckIn !== 'All') {
      result = result.filter(employee => {
        const inOffice = employee.office_premises?.check_in?.in_office?.toLowerCase();
        return filters.inOfficeCheckIn.toLowerCase() === inOffice;
      });
    }

    // Apply In Office CheckOut filter
    if (filters.inOfficeCheckOut !== 'All') {
      result = result.filter(employee => {
        const inOffice = employee.office_premises?.check_out?.in_office?.toLowerCase();
        return filters.inOfficeCheckOut.toLowerCase() === inOffice;
      });
    }

    return result;
  }, [employeeData, filters]);


  const handleStatusChange = (newStatus) => {
    setFilters(prev => ({ ...prev, status: newStatus }));
    if (onPageChange) onPageChange(null, 0);
  };

  const handleLocationChange = (e) => {
    const selectedLocationId = e.target.value;
    const selectedLocation = locations.find(loc => loc.id.toString() === selectedLocationId.toString());

    setFilters(prev => ({
      ...prev,
      locationId: selectedLocationId,
      locationName: selectedLocation ? selectedLocation.location_name : 'All'
    }));
    if (onPageChange) onPageChange(null, 0);
  };

  const handleInOfficeCheckInChange = (newValue) => {
    setFilters(prev => ({ ...prev, inOfficeCheckIn: newValue }));
    if (onPageChange) onPageChange(null, 0);
  };

  const handleInOfficeCheckOutChange = (newValue) => {
    setFilters(prev => ({ ...prev, inOfficeCheckOut: newValue }));
    if (onPageChange) onPageChange(null, 0);
  };

  const handleSearchChange = (searchQuery) => {
    setFilters(prev => ({ ...prev, searchQuery }));
    if (onPageChange) onPageChange(null, 0);
  };

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleFormatChange = (event) => {
    setDownloadFormat(event.target.value);
  };
  const handleResetFilters = () => {
    setFilters({
      searchQuery: '',
      status: 'All',
      location: 'All',
      inOfficeCheckIn: 'All',
      inOfficeCheckOut: 'All',
      locationId: 'All' // Make sure this is included
    });
  };
const token = useSelector(state => state.auth.accessToken);
  const handleDownload = async () => {
    try {
      
      const orgId = localStorage.getItem('organizationId');

      if (!token || !orgId) {
        throw new Error('Authentication token or organization ID not found');
      }

      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const locationParam = filters.locationId === 'All' ? '' : `&location_id=${filters.locationId}`;

      const checkinLocationParam = filters.inOfficeCheckIn === 'All' ? '' :
        `&checkin_location=${filters.inOfficeCheckIn === 'Yes' ? 'In Office' : 'Remote'}`;

      const checkoutLocationParam = filters.inOfficeCheckOut === 'All' ? '' :
        `&checkout_location=${filters.inOfficeCheckOut === 'Yes' ? 'In Office' : 'Remote'}`;

      const endpoint = downloadFormat === 'excel'
        ? `/organization/getDayWiseAttendanceExcel?date=${formattedDate}&status=${filters.status}&organization_id=${orgId}${locationParam}${checkinLocationParam}${checkoutLocationParam}`
        : `/organization/getDayWiseAttendancePDF?date=${formattedDate}&status=${filters.status}&organization_id=${orgId}${locationParam}${checkinLocationParam}${checkoutLocationParam}`

      const response = await axiosInstance({
        ...localAxiosGet(endpoint, token),
        responseType: 'blob'
      });

      if (!(response.data instanceof Blob)) {
        throw new Error('Response data is not a Blob');
      }

      const contentType = response.headers['content-type'] ||
        (downloadFormat === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf');
      const extension = downloadFormat === 'excel' ? 'xlsx' : 'pdf';
      let filename = `attendance_report_${formattedDate.replace(/\//g, '_')}.${extension}`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      setSnackbar({
        open: true,
        message: 'Download started successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        setAnchorEl(null);
        setDownloadFormat('pdf');
      }, 1000);

    } catch (error) {
      console.error('Download failed:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download. Please try again',
        severity: 'error'
      });
    }
  };
  const popoverOpen = Boolean(anchorEl);

  const DatePickerComponent = () => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label="Select Date"
        value={selectedDate}
        onChange={onDateChange}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
        )}
      />
    </LocalizationProvider>
  );

  const headerCellStyle = {
    fontWeight: 600,
    fontSize: '14px',
    background: theme.palette.mode === 'light' ? '#f5f7fa' : '#1e1e2d',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 2000,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    backdropFilter: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '2px',
      backgroundColor: theme.palette.primary.main,
      opacity: 0.2
    }
  };

  if (filteredEmployeeData.length === 0 &&
    !filters.searchQuery &&
    filters.status === 'All' &&
    filters.location === 'All' &&
    filters.inOfficeCheckIn === 'All' &&
    filters.inOfficeCheckOut === 'All') {
    return (
      <Paper elevation={0} sx={{
        p: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper
      }}>
        <Box sx={{
          width: '100%',
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: alpha(theme.palette.primary.light, 0.05),
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Employee Attendance Records
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <DatePickerComponent />
          </Box>
        </Box>

        <SearchOff sx={{ fontSize: 60, color: alpha(theme.palette.text.secondary, 0.4), mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No attendance records found
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400, mb: 3 }}>
          Try changing your filters or selecting a different date to view attendance records.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleResetFilters}
        >
          Clear Filters
        </Button>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={0} sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        background: theme.palette.background.paper,
        position: 'relative',
        width: '100%',
        overflowX: 'auto',
        display: 'block'
      }}>
        <Box sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: alpha(theme.palette.primary.light, 0.05),
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
            Employee Attendance Records
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <DatePickerComponent sx={{ width: { xs: '100%', sm: 'auto' } }} />
          </Box>
        </Box>

        <Box sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.palette.background.paper,
          flexWrap: 'wrap',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <TextField
              placeholder="Search employees..."
              value={filters.searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filters.searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleSearchChange('')}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: '100%', sm: 250 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                }
              }}
            />

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Present">Present</MenuItem>
                <MenuItem value="Absent">Absent</MenuItem>
                <MenuItem value="LateArrival">Late</MenuItem>
                <MenuItem value="Leave">Leave</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Branch</InputLabel>
              <Select
                value={filters.locationId}
                label="Branch"
                onChange={handleLocationChange}
              >
                <MenuItem value="All">All</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id.toString()}>
                    {location.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{
              minWidth: { xs: '100%', sm: 150 },
              display: { xs: 'none', md: 'flex' }
            }}>
              <InputLabel>CheckIn Location</InputLabel>
              <Select
                value={filters.inOfficeCheckIn}
                label="CheckIn Location"
                onChange={(e) => handleInOfficeCheckInChange(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Yes">In Office</MenuItem>
                <MenuItem value="No">Remote</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{
              minWidth: { xs: '100%', sm: 150 },
              display: { xs: 'none', lg: 'flex' }
            }}>
              <InputLabel>CheckOut Location</InputLabel>
              <Select
                value={filters.inOfficeCheckOut}
                label="CheckOut Location"
                onChange={(e) => handleInOfficeCheckOutChange(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Yes">In Office</MenuItem>
                <MenuItem value="No">Remote</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'space-between', sm: 'flex-end' }
          }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '30px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }
              }}
              onClick={handleOpenPopover}
            >
              <FileDownloadOutlinedIcon />
            </Box>

            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<Refresh />}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500
              }}
              onClick={handleResetFilters}
            >
              Reset Filters
            </Button>
          </Box>

          <Popover
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="download-format"
                  name="download-format-group"
                  value={downloadFormat}
                  onChange={handleFormatChange}
                  row
                >
                  <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
                  <FormControlLabel value="excel" control={<Radio />} label="Excel" />
                </RadioGroup>
              </FormControl>
              <Button
                onClick={handleDownload}
                variant="contained"
                sx={{ mt: { xs: 1, sm: 0 } }}
                startIcon={<CloudDownloadIcon />}
              >
                Download
              </Button>
            </Box>
          </Popover>
        </Box>

        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          display: 'block'
        }}>
          {/* Card Layout for small screens */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
            <Grid container spacing={2}>
              {filteredEmployeeData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((employee) => (
                <Grid item xs={12} sm={6} key={employee.user_id}>
                  <Card sx={{
                    p: 2,
                    border: `3px solid ${employee.status.toLowerCase() === 'present'
                        ? theme.palette.success.main
                        : employee.status.toLowerCase() === 'absent'
                          ? theme.palette.error.main
                          : employee.status.toLowerCase() === 'late' || employee.status.toLowerCase() === 'latearrival'
                            ? theme.palette.warning.main
                            : 'transparent'
                      }`,
                    bgcolor: employee.status && employee.status.toLowerCase() === 'absent'
                      ? alpha(theme.palette.error.light, 0.05)
                      : employee.status && (employee.status.toLowerCase() === 'late' || employee.status.toLowerCase() === 'latearrival')
                        ? alpha(theme.palette.warning.light, 0.05)
                        : 'inherit',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease'
                    }
                  }}>
                    {/* Employee Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Checkbox size="small" sx={{ mr: 1 }} />
                      <Avatar
                        src={employee.avatar}
                        alt={employee.name}
                        sx={{
                          mr: 2,
                          width: 40,
                          height: 40,
                          border: `2px solid ${employee.status.toLowerCase() === 'present'
                              ? theme.palette.success.main
                              : employee.status.toLowerCase() === 'absent'
                                ? theme.palette.error.main
                                : employee.status.toLowerCase() === 'late' || employee.status.toLowerCase() === 'latearrival'
                                  ? theme.palette.warning.main
                                  : theme.palette.info.main
                            }`
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {employee.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.emp_id}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={employee.status}
                          size="small"
                          color={getStatusColor(employee.status)}
                          icon={getStatusIcon(employee.status)}
                          sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                        />
                        {employee.checkIn !== '-' &&
                          employee.office_premises?.check_in?.in_office?.toLowerCase() === 'no' && (
                            <Chip
                              label="WFH"
                              size="small"
                              sx={{
                                backgroundColor: 'transparent',
                                color: theme.palette.info.dark,
                                border: `1px solid ${theme.palette.info.light}`,
                                fontSize: '0.6rem'
                              }}
                            />
                          )}
                      </Box>
                    </Box>

                    {/* Time Information */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.primary.main }}>
                        Time Details
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime fontSize="small" color="primary" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Check In</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                {employee.checkIn}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime fontSize="small" color="secondary" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Check Out</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                {employee.checkOut}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Office Location Information */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.primary.main }}>
                        Office Location
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {employee.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? (
                              <LocationOn fontSize="small" color="success" />
                            ) : (
                              <LocationOff fontSize="small" color="error" />
                            )}
                            <Box>
                              <Typography variant="caption" color="text.secondary">Check In</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {employee.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? 'In Office' : 'Remote'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {employee.checkOut !== '-' && employee.office_premises?.check_out?.in_office !== "" ? (
                              employee.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? (
                                <LocationOn fontSize="small" color="success" />
                              ) : (
                                <LocationOff fontSize="small" color="error" />
                              )
                            ) : (
                              <LocationOff fontSize="small" color="disabled" />
                            )}
                            <Box>
                              <Typography variant="caption" color="text.secondary">Check Out</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {employee.checkOut !== '-' && employee.office_premises?.check_out?.in_office !== "" ? (
                                  employee.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? 'In Office' : 'Remote'
                                ) : '-'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Production Hours and Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime fontSize="small" color="info" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Production Hours</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {employee.productionHours}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View attendance history">
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
                            }}
                            onClick={(e) => handleViewHistory(employee, e)}
                          >
                            <History fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit attendance">
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Edit fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Empty State for Cards */}
            {filteredEmployeeData.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 5 }}>
                <SearchOff sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.4) }} />
                <Typography variant="h6" color="text.secondary">No matching employees found</Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {filters.status !== 'All' || filters.location !== 'All' || filters.inOfficeCheckIn !== 'All' || filters.inOfficeCheckOut !== 'All'
                    ? `No employees match your current filters`
                    : 'Try adjusting your search query'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => setFilters({
                    searchQuery: '',
                    status: 'All',
                    location: 'All',
                    inOfficeCheckIn: 'All',
                    inOfficeCheckOut: 'All'
                  })}
                  sx={{ mt: 1 }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Box>

          {/* Table Layout for medium and large screens */}
          <TableContainer sx={{
            display: { xs: 'none', md: 'block' },
            // maxHeight: 'calc(100vh - 250px)',
            overflow: 'auto',
            position: 'relative',
            isolation: 'isolate',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
            },
            transform: 'translateZ(0)',
            minWidth: { xs: 800, sm: '100%' }
          }}>
            <Table sx={{
              minWidth: 800,
              position: 'relative',
              borderCollapse: 'separate',
              borderSpacing: 0,
              '& .MuiTableCell-root': {
                position: 'relative',
                backgroundColor: theme.palette.background.paper
              },
              // Responsive table styles
              [`@media (max-width:${theme.breakpoints.values.sm}px)`]: {
                '& .MuiTableCell-root': {
                  padding: '8px 4px',
                  '&:first-of-type': {
                    paddingLeft: '8px'
                  },
                  '&:last-of-type': {
                    paddingRight: '8px'
                  }
                }
              }
            }} size="medium" stickyHeader>
              <TableHead sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1900,
                transform: 'translateZ(0)'
              }}>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ ...headerCellStyle, pl: { xs: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox size="small" color="primary" />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: { lg: '15%', md: '20%' } }}>Employee</TableCell>

                  {/* Status column - hidden below lg breakpoint */}
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: '15%',
                    display: { xs: 'none', lg: 'table-cell' }
                  }}>
                    Status
                  </TableCell>

                  {/* Combined Time columns for md and below */}
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: { md: '25%' },
                    display: { lg: 'none', md: 'table-cell', xs: 'table-cell' },
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    Check In / Out
                  </TableCell>

                  {/* Separate columns for lg and above */}
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: '12%',
                    display: { xs: 'none', lg: 'table-cell' }
                  }}>
                    Check In
                  </TableCell>
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: '12%',
                    display: { xs: 'none', lg: 'table-cell' }
                  }}>
                    Check Out
                  </TableCell>

                  {/* Combined Office Location columns for md and below */}
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: { md: '25%', sm: '20%' },
                    display: { lg: 'none', sm: 'table-cell', xs: 'none' },
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    Office Location
                  </TableCell>

                  {/* Separate Office columns for lg and above */}
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: '12%',
                    display: { xs: 'none', lg: 'table-cell' },
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    In Office CheckIn
                  </TableCell>
                  <TableCell sx={{
                    ...headerCellStyle,
                    width: '12%',
                    display: { xs: 'none', lg: 'table-cell' },
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    In Office CheckOut
                  </TableCell>

                  <TableCell sx={{
                    ...headerCellStyle,
                    width: '12%',
                    display: { xs: 'none', sm: 'table-cell' },
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    Production Hours
                  </TableCell>
                  <TableCell align="center" sx={{ ...headerCellStyle, pr: { xs: 2, sm: 4 }, width: '12%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredEmployeeData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((employee) => (
                  <TableRow
                    key={employee.user_id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.light, 0.05),
                        transform: 'translateY(-1px)',
                        transition: 'transform 0.2s ease-in-out',
                        cursor: 'pointer'
                      },
                      bgcolor: employee.status && employee.status.toLowerCase() === 'absent'
                        ? alpha(theme.palette.error.light, 0.05)
                        : employee.status && (employee.status.toLowerCase() === 'late' || employee.status.toLowerCase() === 'latearrival')
                          ? alpha(theme.palette.warning.light, 0.05)
                          : 'inherit',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      borderLeft: employee.status.toLowerCase() === 'present'
                        ? `3px solid ${theme.palette.success.main}`
                        : employee.status.toLowerCase() === 'absent'
                          ? `3px solid ${theme.palette.error.main}`
                          : employee.status.toLowerCase() === 'late' || employee.status.toLowerCase() === 'latearrival'
                            ? `3px solid ${theme.palette.warning.main}`
                            : `3px solid transparent`
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ pl: { xs: 1 } }}>
                      <Checkbox
                        size="small"
                        color="primary"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                            borderRadius: 0.5
                          }
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={employee.avatar}
                          alt={employee.name}
                          sx={{
                            mr: 1,
                            width: { xs: 32 },
                            height: { xs: 32 },
                            border: `2px solid ${employee.status.toLowerCase() === 'present'
                              ? theme.palette.success.main
                              : employee.status.toLowerCase() === 'absent'
                                ? theme.palette.error.main
                                : employee.status.toLowerCase() === 'late' || employee.status.toLowerCase() === 'latearrival'
                                  ? theme.palette.warning.main
                                  : theme.palette.info.main
                              }`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1)'
                            }
                          }}
                        />
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="body1" fontWeight="600" sx={{
                              color: theme.palette.text.primary,
                              fontSize: { xs: '0.8rem' }
                            }}>
                              {employee.name}
                            </Typography>

                            {/* Status chip - only show below lg breakpoint */}
                            <Chip
                              label={employee.status}
                              size="small"
                              color={getStatusColor(employee.status)}
                              icon={getStatusIcon(employee.status)}
                              sx={{
                                display: { lg: 'none', xs: 'inline-flex' },
                                fontWeight: 600,
                                fontSize: '0.5rem',
                                borderRadius: 1,
                                '& .MuiChip-label': { px: 1 },
                                '& .MuiChip-icon': { color: 'inherit' },
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              }}
                            />

                            {employee.checkIn !== '-' &&
                              employee.office_premises?.check_in?.in_office?.toLowerCase() === 'no' && (
                                <Chip
                                  label="WFH"
                                  size="small"
                                  sx={{
                                    backgroundColor: 'transparent',
                                    color: theme.palette.info.dark,
                                    border: `1px solid ${theme.palette.info.light}`,
                                    '& .MuiChip-label': {
                                      fontSize: '0.5rem',
                                      lineHeight: '1',
                                    },
                                    display: { xs: 'none', sm: 'inline-flex' }
                                  }}
                                />
                              )}
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{
                              fontSize: '0.25',
                              fontWeight: 500,
                              letterSpacing: '0.01em'
                            }}>
                              {employee.department}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Status column - only show at lg and above */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Chip
                        label={employee.status}
                        size="small"
                        color={getStatusColor(employee.status)}
                        icon={getStatusIcon(employee.status)}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.6rem',
                          borderRadius: 1,
                          '& .MuiChip-label': { px: 1 },
                          '& .MuiChip-icon': { color: 'inherit' },
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                      />
                    </TableCell>

                    {/* Combined Check In/Out column - show below lg breakpoint */}
                    <TableCell sx={{ display: { lg: 'none', md: 'table-cell', xs: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          opacity: employee.status.toLowerCase() === 'absent' ? 0.5 : 1
                        }}>
                          <AccessTime
                            fontSize="small"
                            sx={{
                              mr: 1,
                              fontSize: 14,
                              color: employee.status.toLowerCase() === 'absent'
                                ? theme.palette.text.disabled
                                : theme.palette.primary.main,
                              opacity: 0.7,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontFamily: 'monospace',
                              color: employee.status.toLowerCase() === 'absent'
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                              fontSize: '0.7rem'
                            }}
                          >
                            In: {employee.checkIn}
                          </Typography>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          opacity: employee.status.toLowerCase() === 'absent' ? 0.5 : 1
                        }}>
                          <AccessTime
                            fontSize="small"
                            sx={{
                              mr: 1,
                              fontSize: 14,
                              color: employee.status.toLowerCase() === 'absent'
                                ? theme.palette.text.disabled
                                : theme.palette.secondary.main,
                              opacity: 0.7,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontFamily: 'monospace',
                              color: employee.status.toLowerCase() === 'absent'
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                              fontSize: '0.7rem'
                            }}
                          >
                            Out: {employee.checkOut}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Separate Check In column - only show at lg and above */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: employee.status.toLowerCase() === 'absent' ? 0.5 : 1
                      }}>
                        <AccessTime
                          fontSize="small"
                          sx={{
                            mr: 1,
                            fontSize: 16,
                            color: employee.status.toLowerCase() === 'absent'
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                            opacity: 0.7,
                            display: { xs: 'none', sm: 'block' }
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            color: employee.status.toLowerCase() === 'absent'
                              ? theme.palette.text.disabled
                              : theme.palette.text.primary,
                            fontSize: { xs: '0.75rem' }
                          }}
                        >
                          {employee.checkIn}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Separate Check Out column - only show at lg and above */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: employee.status.toLowerCase() === 'absent' ? 0.5 : 1
                      }}>
                        <AccessTime
                          fontSize="small"
                          sx={{
                            mr: 1,
                            fontSize: 16,
                            color: employee.status.toLowerCase() === 'absent'
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                            opacity: 0.7,
                            display: { xs: 'none', sm: 'block' }
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            color: employee.status.toLowerCase() === 'absent'
                              ? theme.palette.text.disabled
                              : theme.palette.text.primary,
                            fontSize: { xs: '0.75rem' }
                          }}
                        >
                          {employee.checkOut}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Combined Office Location column - show below lg breakpoint */}
                    <TableCell sx={{ display: { lg: 'none', sm: 'table-cell', xs: 'none' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Check In Office Location */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {employee.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? (
                            <Tooltip title={`IP: ${employee.office_premises?.check_in?.ip || 'N/A'}`} arrow>
                              <LocationOn fontSize="small" color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={employee.office_premises?.check_in?.ip ? `IP: ${employee.office_premises.check_in.ip}` : 'No check-in data'} arrow>
                              <LocationOff fontSize="small" color="error" />
                            </Tooltip>
                          )}
                          <Typography variant="body2" sx={{ fontSize: '11px' }}>
                            In: {employee.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? 'Yes' : 'No'}
                          </Typography>
                        </Box>

                        {/* Check Out Office Location */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {employee.checkOut !== '-' && employee.office_premises?.check_out?.in_office !== "" ? (
                            <>
                              {employee.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? (
                                <Tooltip title={`IP: ${employee.office_premises?.check_out?.ip || 'N/A'}`} arrow>
                                  <LocationOn fontSize="small" color="success" />
                                </Tooltip>
                              ) : (
                                <Tooltip title={employee.office_premises?.check_out?.ip ? `IP: ${employee.office_premises.check_out.ip}` : 'Remote checkout'} arrow>
                                  <LocationOff fontSize="small" color="error" />
                                </Tooltip>
                              )}
                              <Typography variant="body2" sx={{ fontSize: '11px' }}>
                                Out: {employee.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? 'Yes' : 'No'}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '11px' }}>
                              Out: -
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Separate Office Check In column - only show at lg and above */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {employee.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? (
                          <Tooltip title={`IP: ${employee.office_premises?.check_in?.ip || 'N/A'}`} arrow>
                            <LocationOn fontSize="small" color="success" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={employee.office_premises?.check_in?.ip ? `IP: ${employee.office_premises.check_in.ip}` : 'No check-in data'} arrow>
                            <LocationOff fontSize="small" color="error" />
                          </Tooltip>
                        )}
                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                          {employee.office_premises?.check_in?.in_office?.toLowerCase() === 'yes' ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Separate Office Check Out column - only show at lg and above */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Only show content if there's actual checkout data */}
                        {employee.checkOut !== '-' && employee.office_premises?.check_out?.in_office !== "" ? (
                          <>
                            {employee.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? (
                              <Tooltip title={`IP: ${employee.office_premises?.check_out?.ip || 'N/A'}`} arrow>
                                <LocationOn fontSize="small" color="success" />
                              </Tooltip>
                            ) : (
                              <Tooltip title={employee.office_premises?.check_out?.ip ? `IP: ${employee.office_premises.check_out.ip}` : 'Remote checkout'} arrow>
                                <LocationOff fontSize="small" color="error" />
                              </Tooltip>
                            )}
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {employee.office_premises?.check_out?.in_office?.toLowerCase() === 'yes' ? 'Yes' : 'No'}
                            </Typography>
                          </>
                        ) : (
                          /* Show empty state when no checkout has occurred */
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        opacity: employee.status.toLowerCase() === 'absent' ? 0.5 : 1
                      }}>
                        <AccessTime
                          fontSize="small"
                          sx={{
                            mr: 1,
                            fontSize: 16,
                            color: employee.status.toLowerCase() === 'absent'
                              ? theme.palette.text.disabled
                              : theme.palette.info.main,
                            opacity: 0.7
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            color: employee.status.toLowerCase() === 'absent'
                              ? theme.palette.text.disabled
                              : theme.palette.text.primary
                          }}
                        >
                          {employee.productionHours}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="right" sx={{ pr: { xs: 2, sm: 4 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                        <Tooltip title="View attendance history">
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                transform: 'translateY(-2px)'
                              },
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              display: { xs: 'none', sm: 'inline-flex' }
                            }}
                            onClick={(e) => handleViewHistory(employee, e)}
                          >
                            <History fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit attendance">
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                transform: 'translateY(-2px)'
                              },
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Edit fontSize="small" color="primary" sx={{ fontSize: '14px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {filteredEmployeeData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <SearchOff sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.4) }} />
                        <Typography variant="h6" color="text.secondary">
                          No matching employees found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {filters.status !== 'All' || filters.location !== 'All' || filters.inOfficeCheckIn !== 'All' || filters.inOfficeCheckOut !== 'All'
                            ? `No employees match your current filters`
                            : 'Try adjusting your search query'}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Refresh />}
                          onClick={() => setFilters({
                            searchQuery: '',
                            status: 'All',
                            location: 'All',
                            inOfficeCheckIn: 'All',
                            inOfficeCheckOut: 'All'
                          })}
                          sx={{ mt: 1 }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 1, sm: 3 },
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Group sx={{ fontSize: 18, mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
              Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredEmployeeData.length)} of {filteredEmployeeData.length} employees
            </Typography>
          </Box>
          <TablePagination
            component="div"
            count={filteredEmployeeData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              '& .MuiTablePagination-select': {
                borderRadius: 1,
                mr: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:focus': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              },
              '& .MuiTablePagination-selectIcon': {
                width: 20,
                height: 20,
                color: theme.palette.primary.main
              },
              '& .MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 1,
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  }
                }
              }
            }}
          />
        </Box>
      </Paper>

      {/* {selectedEmployee && (
        <EmployeeAttendanceHistory
          employee={selectedEmployee}
          open={historyDialogOpen}
          onClose={() => {
            setHistoryDialogOpen(false);
            setSelectedEmployee(null);
          }}
        />
      )} */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
const AttendanceManagement = () => {
  const theme = useTheme();
  const [attendanceData, setAttendanceData] = useState({
    summary: {
      total: 0,
      present: { count: 0, percentage: '0%' },
      lateLogin: { count: 0, percentage: '0%' },
      absent: { count: 0, percentage: '0%' },
      late: { count: 0, percentage: '0%' }

    },
    employees: [],
  });
  const [monthlyReportDialogOpen, setMonthlyReportDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [displayApiMessage, setDisplayApiMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('')

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMyAttendance, setShowMyAttendance] = useState(false);
  const [filters, setFilters] = useState(() => {
    const storedOrgId = localStorage.getItem('organizationId');

    return {
      selectedDate: new Date(),
      department: '',
      status: '',
      viewMode: 'all',
      searchQuery: '',
      rowsPerPage: 10,
      page: 0,
      organizationId: storedOrgId ? parseInt(storedOrgId) : null
    };
  });

  const token = useSelector(state => state.auth.accessToken);
  const getAuthToken = () => {
    return token;
  };

  const handleViewEmployeeHistory = (employee) => {
    setSelectedEmployee(employee);
    setHistoryDialogOpen(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
  };

  const fetchAttendanceOverview = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!filters.organizationId) {
        throw new Error('Organization ID not found');
      }

      const endpoint = `/organization/getTodayAttendanceOverview/${filters.organizationId}`;
      const config = localAxiosGet(endpoint, token);
      const response = await axiosInstance(config);

      if (response.data && response.data.status === "SUCCESS") {
        const apiData = response.data.message;

        const total = apiData.totalEmployees || 0;
        const presentCount = apiData.presentToday || 0;
        const lateCount = apiData.lateArrivals || 0;
        const absentCount = apiData.absentToday || 0;
        const leaveCount = apiData.leaveToday || 0;

        const presentPercentage = total > 0 ? `${Math.round((presentCount / total) * 100)}%` : '0%';
        const latePercentage = total > 0 ? `${Math.round((lateCount / total) * 100)}%` : '0%';
        const absentPercentage = total > 0 ? `${Math.round((absentCount / total) * 100)}%` : '0%';
        const leavePercentage = total > 0 ? `${Math.round((leaveCount / total) * 100)}%` : '0%';

        setAttendanceData(prevData => ({
          ...prevData,
          summary: {
            total,
            present: { count: presentCount, percentage: presentPercentage },
            lateLogin: { count: lateCount, percentage: latePercentage },
            absent: { count: absentCount, percentage: absentPercentage },
            leave: { count: leaveCount, percentage: leavePercentage } // Ensure leave has both properties
          }
        }));

        await fetchEmployeeAttendance();
      } else {
        throw new Error(response.data?.message || 'Failed to fetch attendance data');
      }
    } catch (err) {
      console.error('Error fetching attendance overview:', err);
      setError(err.message || 'Failed to fetch attendance data');
      setAttendanceData({
        summary: {
          total: 0,
          present: { count: 0, percentage: '0%' },
          lateLogin: { count: 0, percentage: '0%' },
          absent: { count: 0, percentage: '0%' },
          leave: { count: 0, percentage: '0%' } // Ensure leave has default values
        },
        employees: []
      });
    } finally {
      setLoading(false);
    }
  }, [filters.organizationId]);

  const fetchEmployeeAttendance = useCallback(async (customDate) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!filters.organizationId) {
        throw new Error('Organization ID not found');
      }

      const dateToUse = customDate || filters.selectedDate;

      const formattedDate = `${String(dateToUse.getMonth() + 1).padStart(2, '0')}/${String(dateToUse.getDate()).padStart(2, '0')}/${dateToUse.getFullYear()}`;


      const endpoint = `/organization/getAllEmployeesForHrAttendence?Date=${formattedDate}&organization_id=${filters.organizationId}`;

      const config = localAxiosGet(endpoint, token);
      const response = await axiosInstance(config);

      if (response.data && response.data.status === "SUCCESS") {
        const employeeRecords = response.data.message.employeeRecords || [];

        const formattedEmployees = employeeRecords.map((emp) => {

          const checkInTime = emp.check_in && emp.check_in !== null
            ? new Date(emp.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '-';

          const checkOutTime = emp.check_out && emp.check_out !== null
            ? new Date(emp.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '-';


          let productionHours = '0h 00m';
          let isPositive = false;

          if (emp.total_hours && emp.total_hours !== '00:00:00' && emp.total_hours !== null) {
            const [hours, minutes] = emp.total_hours.split(':');
            productionHours = `${hours}h ${minutes}m`;
            isPositive = (parseInt(hours) >= 7 || (parseInt(hours) === 6 && parseInt(minutes) >= 30));
          }


          let breakTime = '-';
          if (emp.break_duration) {
            const breakDurationValue = parseFloat(emp.break_duration);

            if (!isNaN(breakDurationValue)) {
              breakTime = `${Math.round(breakDurationValue)}m`;
            } else if (typeof emp.break_duration === 'string') {
              const minutes = parseInt(emp.break_duration);
              if (!isNaN(minutes)) {
                breakTime = `${minutes}m`;
              } else {
                breakTime = emp.break_duration;
              }
            }
          }


          const officePremises = emp.office_premises || {
            check_in: { ip: null, in_office: "" },
            check_out: { ip: null, in_office: "" }
          };

          return {
            user_id: emp.user_id,
            name: emp.name,
            emp_id:emp.emp_id,
            status: emp.status,
            checkIn: checkInTime,
            checkOut: checkOutTime,
            break: breakTime,
            productionHours: productionHours,
            isPositive: isPositive,
            department: emp.department,
            location: emp.location || 'Unknown',
            office_premises: {
              check_in: {
                ip: officePremises.check_in?.ip || null,
                in_office: officePremises.check_in?.in_office || "no" // Default to "no" if missing
              },
              check_out: {
                ip: officePremises.check_out?.ip || null,
                in_office: officePremises.check_out?.in_office || "no" // Default to "no" if missing
              }
            }
          };
        });

        setAttendanceData(prevData => ({
          ...prevData,
          employees: formattedEmployees
        }));
      } else {
        throw new Error(response.data?.message?.msg || 'Failed to fetch employee attendance data');
      }
    } catch (err) {
      console.error('Error fetching employee attendance:', err);
      setError(err.message || 'Failed to fetch employee attendance data');
      setAttendanceData(prevData => ({
        ...prevData,
        employees: []
      }));
    } finally {
      setLoading(false);
    }
  }, [filters.organizationId, filters.selectedDate]);
  useEffect(() => {
    fetchAttendanceOverview();
  }, [fetchAttendanceOverview]);


  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
      page: filterName !== 'page' ? 0 : value
    }));


    if (filterName !== 'page') {

      const timer = setTimeout(() => {
        fetchEmployeeAttendance();
      }, 500);

      return () => clearTimeout(timer);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setFilters(prev => ({
        ...prev,
        selectedDate: date,
        page: 0
      }));


      const timer = setTimeout(() => {
        fetchEmployeeAttendance(date);
      }, 300);

      return () => clearTimeout(timer);
    }
  };

  const handleRefresh = () => {
    fetchAttendanceOverview();
  };

  const handlePageChange = (event, newPage) => {
    handleFilterChange('page', newPage);
  };

  const handleRowsPerPageChange = (event) => {
    handleFilterChange('rowsPerPage', parseInt(event.target.value, 10));
  };


  const getFilteredEmployees = useCallback(() => {
    let filteredEmployees = [...attendanceData.employees];

    if (filters.searchQuery) {
      filteredEmployees = filteredEmployees.filter(
        employee => employee.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          (employee.department && employee.department.toLowerCase().includes(filters.searchQuery.toLowerCase()))
      );
    }

    return filteredEmployees;
  }, [attendanceData.employees, filters.searchQuery]);


  //Monthly Attendance Download related code
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, name: 'January' },
    { value: 2, name: 'February' },
    { value: 3, name: 'March' },
    { value: 4, name: 'April' },
    { value: 5, name: 'May' },
    { value: 6, name: 'June' },
    { value: 7, name: 'July' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'October' },
    { value: 11, name: 'November' },
    { value: 12, name: 'December' },
  ];
  const storedOrgId = localStorage.getItem('organizationId');
  const handleMonthlyAttendanceDownload = () => {
    setMonthlyReportDialogOpen(true)
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(currentYear);
  }

  const handleCloseDialog = () => {
    setMonthlyReportDialogOpen(false);
    setDisplayApiMessage("");
    setDownloadFormat('')
  }

  const handleDownloadReport = async () => {
    try {
      const endpoint = downloadFormat === 'excel'
        ? `/organization/getMonthlyAttendanceExcel?month=${selectedMonth}&year=${selectedYear}&organization_id=${storedOrgId}`
        : `/organization/getMonthlyAttendancePDF?month=${selectedMonth}&year=${selectedYear}&organization_id=${storedOrgId}`;

      // Use your localAxiosGet with responseType in options
      const response = await axiosInstance({
        ...localAxiosGet(endpoint, token),
        responseType: 'blob' // This is crucial for Excel files
      });

      // Verify the response data
      if (!(response.data instanceof Blob)) {
        throw new Error('Response data is not a Blob');
      }

      // Determine content type and extension
      const contentType = response.headers['content-type'] ||
        (downloadFormat === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf');

      const extension = downloadFormat === 'excel' ? 'xlsx' : 'pdf';

      // Extract filename from headers or use default
      let filename = `attendance_report_${selectedMonth}_${selectedYear}.${extension}`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create and trigger download
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      setDisplayApiMessage("Download started successfully");
      setIsError(false);

      setTimeout(() => {
        setMonthlyReportDialogOpen(false);
        setDisplayApiMessage("");
        setDownloadFormat('')
      }, 6000);

    } catch (error) {
      setDisplayApiMessage("Error downloading file");
      setIsError(true);

      // Enhanced error logging
      console.error('Download error:', {
        error,
        response: error.response,
        config: error.config
      });

      if (error.response?.data instanceof Blob) {
        // If we got a blob error response, try to read it
        const errorText = await error.response.data.text();
        console.error('Error response content:', errorText);
      }
    }
  };
  const storedUserId = localStorage.getItem('userId')
  const handleMyAttendance = () => {
    setShowMyAttendance(true);
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Attendance Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor and manage employee attendance records
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mr: 3 }}>
          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={handleMonthlyAttendanceDownload}
          >
            Attendance Report
          </Button>
          <Button
            variant="contained"
            startIcon={<EventAvailableIcon />}
            onClick={handleMyAttendance}
          >
            My Attendance
          </Button>
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <AttendanceSummary summaryData={attendanceData.summary} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
          <Button variant="text" size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      ) : (
        <AttendanceTable
          employeeData={getFilteredEmployees()}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          selectedDate={filters.selectedDate}
          onDateChange={handleDateChange}
          onViewEmployeeHistory={handleViewEmployeeHistory}
        />
      )}

      {selectedEmployee && (
        <EmployeeAttendanceHistory
          employee={selectedEmployee}
          open={historyDialogOpen}
          onClose={handleCloseHistoryDialog}
        />
      )}

      <Dialog open={monthlyReportDialogOpen} onClose={handleCloseDialog}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start'
          },
          '& .MuiPaper-root': {
            width: '500px',
            maxWidth: '90vw',
            p: 4,
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{
          p: 3,
          fontWeight: 600,
          fontSize: '1.25rem',

        }}>
          Download Monthly Attendance Report
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* First row - Month and Year */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mt: 0.5 }}>
                <InputLabel id="month-select-label">Month</InputLabel>
                <Select
                  labelId="month-select-label"
                  id="month-select"
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mt: 0.5 }}>
                <InputLabel id="year-select-label">Year</InputLabel>
                <Select
                  labelId="year-select-label"
                  id="year-select"
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Second row - Format selection (full width) */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id='select-download-format'>Select Format</InputLabel>
                <Select
                  labelId='select-download-format'
                  id='select-format'
                  value={downloadFormat}
                  label='Select Format'
                  onChange={(e) => setDownloadFormat(e.target.value)}
                >
                  <MenuItem value='excel'>Excel</MenuItem>
                  <MenuItem value='pdf'>PDF</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            {displayApiMessage && (
              <Typography
                sx={{
                  color: isError ? 'error.main' : 'success.main',
                  flexGrow: 1
                }}
              >
                {displayApiMessage}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseDialog} variant='outlined'>Cancel</Button>
              <Button onClick={handleDownloadReport} variant="contained" startIcon={<GetAppIcon />}>
                Download
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      <Modal
        open={showMyAttendance}
        onClose={() => setShowMyAttendance(false)}
        aria-labelledby="my-attendance-modal"
        aria-describedby="display-employee-attendance-calendar"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 1200,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          maxHeight: '90vh',
          position: 'relative'
        }}>
          <IconButton

            onClick={() => setShowMyAttendance(false)}
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              bgcolor: '#E0E0E0',
              '&:hover': {
                bgcolor: '#CCCCCC',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s',
              zIndex: 1000
            }}
          >
            <Close fontSize="small" />
          </IconButton>

          <EmployeeAttendanceCalendar userId={storedUserId} />
        </Box>
      </Modal>

    </Box>
  );
};

export default AttendanceManagement;